import React, { useState, useRef, useEffect } from 'react';
import { FolderTree, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Input } from '../ui/input';

/**
 * File tree sidebar component with context menu for rename/delete
 */
export const FileTreePanel = ({
    fileContents,
    activeFile,
    onFileSelect,
    onCreateFile,
    onRenameFile,
    onDeleteFile
}) => {
    const files = Object.keys(fileContents);
    
    // Context menu state
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, file: null });
    const [renameMode, setRenameMode] = useState({ active: false, file: null, newName: '' });
    const [deleteConfirm, setDeleteConfirm] = useState({ visible: false, file: null });
    
    const contextMenuRef = useRef(null);
    const renameInputRef = useRef(null);
    
    // Get file icon based on extension
    const getFileIcon = (filename) => {
        const ext = filename.split('.').pop()?.toLowerCase();
        const iconMap = {
            jsx: '⚛️',
            js: '📜',
            tsx: '💠',
            ts: '💠',
            css: '🎨',
            html: '🌐',
            json: '📋',
            md: '📝',
            py: '🐍'
        };
        return iconMap[ext] || '📄';
    };

    // Handle right-click on file
    const handleContextMenu = (e, file) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Calculate position relative to the viewport
        const x = e.clientX;
        const y = e.clientY;
        
        setContextMenu({ visible: true, x, y, file });
    };

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
                setContextMenu({ visible: false, x: 0, y: 0, file: null });
            }
        };

        if (contextMenu.visible) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [contextMenu.visible]);

    // Focus rename input when entering rename mode
    useEffect(() => {
        if (renameMode.active && renameInputRef.current) {
            renameInputRef.current.focus();
            // Select filename without extension
            const dotIndex = renameMode.newName.lastIndexOf('.');
            if (dotIndex > 0) {
                renameInputRef.current.setSelectionRange(0, dotIndex);
            } else {
                renameInputRef.current.select();
            }
        }
    }, [renameMode.active]);

    // Start rename mode
    const handleRenameStart = () => {
        setRenameMode({ active: true, file: contextMenu.file, newName: contextMenu.file });
        setContextMenu({ visible: false, x: 0, y: 0, file: null });
    };

    // Confirm rename
    const handleRenameConfirm = () => {
        const oldName = renameMode.file;
        const newName = renameMode.newName.trim();
        
        if (newName && newName !== oldName && !files.includes(newName)) {
            if (onRenameFile) {
                onRenameFile(oldName, newName);
            }
        }
        setRenameMode({ active: false, file: null, newName: '' });
    };

    // Cancel rename
    const handleRenameCancel = () => {
        setRenameMode({ active: false, file: null, newName: '' });
    };

    // Handle rename input keydown
    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleRenameConfirm();
        } else if (e.key === 'Escape') {
            handleRenameCancel();
        }
    };

    // Show delete confirmation
    const handleDeleteStart = () => {
        setDeleteConfirm({ visible: true, file: contextMenu.file });
        setContextMenu({ visible: false, x: 0, y: 0, file: null });
    };

    // Confirm delete
    const handleDeleteConfirm = () => {
        if (onDeleteFile && deleteConfirm.file) {
            onDeleteFile(deleteConfirm.file);
        }
        setDeleteConfirm({ visible: false, file: null });
    };

    // Cancel delete
    const handleDeleteCancel = () => {
        setDeleteConfirm({ visible: false, file: null });
    };

    return (
        <div className="h-full flex flex-col bg-gray-50 border-r border-gray-200 relative">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FolderTree className="w-4 h-4" />
                    Files
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={onCreateFile}
                    title="New file"
                    data-testid="create-file-btn"
                >
                    <Plus className="w-4 h-4" />
                </Button>
            </div>
            
            {/* File list */}
            <ScrollArea className="flex-1">
                <div className="p-2 space-y-0.5">
                    {files.length === 0 ? (
                        <div className="text-xs text-gray-400 p-2 text-center">
                            No files yet
                        </div>
                    ) : (
                        files.map((file) => (
                            <div key={file}>
                                {renameMode.active && renameMode.file === file ? (
                                    // Rename input mode
                                    <div className="flex items-center gap-1 px-1">
                                        <span className="text-xs">{getFileIcon(renameMode.newName || file)}</span>
                                        <Input
                                            ref={renameInputRef}
                                            value={renameMode.newName}
                                            onChange={(e) => setRenameMode(prev => ({ ...prev, newName: e.target.value }))}
                                            onKeyDown={handleRenameKeyDown}
                                            onBlur={handleRenameConfirm}
                                            className="h-7 text-sm py-0 px-1"
                                            data-testid="rename-input"
                                        />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-50"
                                            onClick={handleRenameConfirm}
                                            data-testid="rename-confirm-btn"
                                        >
                                            <Check className="w-3 h-3" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-gray-400 hover:text-gray-600"
                                            onClick={handleRenameCancel}
                                            data-testid="rename-cancel-btn"
                                        >
                                            <X className="w-3 h-3" />
                                        </Button>
                                    </div>
                                ) : (
                                    // Normal file display
                                    <button
                                        onClick={() => onFileSelect(file)}
                                        onContextMenu={(e) => handleContextMenu(e, file)}
                                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left transition-colors ${
                                            activeFile === file
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                        data-testid={`file-item-${file}`}
                                    >
                                        <span className="text-xs">{getFileIcon(file)}</span>
                                        <span className="truncate">{file}</span>
                                    </button>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </ScrollArea>

            {/* Context Menu */}
            {contextMenu.visible && (
                <div
                    ref={contextMenuRef}
                    className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[140px]"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                    data-testid="file-context-menu"
                >
                    <button
                        onClick={handleRenameStart}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        data-testid="context-rename-btn"
                    >
                        <Pencil className="w-4 h-4" />
                        Rename
                    </button>
                    <button
                        onClick={handleDeleteStart}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        data-testid="context-delete-btn"
                    >
                        <Trash2 className="w-4 h-4" />
                        Delete
                    </button>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm.visible && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-confirm-modal">
                    <div className="bg-white rounded-lg shadow-xl p-4 max-w-sm mx-4">
                        <h3 className="font-medium text-gray-900 mb-2">Șterge fișierul?</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Ești sigur că vrei să ștergi <strong>{deleteConfirm.file}</strong>? Această acțiune nu poate fi anulată.
                        </p>
                        <div className="flex gap-2 justify-end">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDeleteCancel}
                                data-testid="delete-cancel-btn"
                            >
                                Anulează
                            </Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleDeleteConfirm}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="delete-confirm-btn"
                            >
                                Șterge
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileTreePanel;
